import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Facebook, Twitter, Send, Share2, Instagram } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { shareStoryImage } from "@/lib/share";
import StoryPreviewButton from "@/components/StoryPreviewButton";

export default function ColumnDetail() {
  const { slug } = useParams();
  const [col, setCol] = useState(null);
  useEffect(() => { api.get(`/columns/by-slug/${slug}`).then((r) => setCol(r.data)); }, [slug]);
  if (!col) return <div className="max-w-3xl mx-auto py-10 px-4">Yükleniyor...</div>;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  // Social crawlers go through the backend OG endpoint so they pick up the
  // columnist avatar, article title and first paragraph instead of the SPA logo.
  const shareUrl = `${backendUrl}/api/og/column/${col.slug}`;
  const storyUrl = `${backendUrl}/api/story/column/${col.slug}`;
  const fullUrl = `${window.location.origin}/yazi/${col.slug}`;
  const enc = encodeURIComponent;
  const shareTitle = enc(col.title);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("Link kopyalandı");
    } catch {
      toast.error("Kopyalanamadı");
    }
  };

  return (
    <article className="max-w-3xl mx-auto px-4 py-6" data-testid="column-detail">
      {col.columnist && (
        <Link to={`/kose-yazilari/${col.columnist.slug}`} className="flex items-center gap-2 mb-3">
          <img src={col.columnist.photo} alt={col.columnist.name} className="w-10 h-10 rounded-full" />
          <div>
            <div className="font-bold text-sm">{col.columnist.name}</div>
            <div className="text-xs text-gray-500">{new Date(col.publish_at).toLocaleString("tr-TR")}</div>
          </div>
        </Link>
      )}
      <h1 className="text-3xl md:text-4xl font-black leading-tight">{col.title}</h1>
      {col.cover_image && <img src={col.cover_image} alt="" className="w-full aspect-[16/9] object-cover rounded-lg mt-5" />}
      <div className="prose prose-lg mt-6 whitespace-pre-wrap">{col.content}</div>

      {/* Share bar */}
      <div className="mt-8 pt-5 border-t flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-700 mr-1">
          <Share2 className="w-3.5 h-3.5" /> Paylaş
        </span>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`}
          target="_blank" rel="noreferrer"
          aria-label="Facebook'ta paylaş"
          data-testid="column-share-facebook"
          className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
        ><Facebook className="w-4 h-4" /></a>
        <a
          href={`https://twitter.com/intent/tweet?text=${shareTitle}&url=${enc(shareUrl)}`}
          target="_blank" rel="noreferrer"
          aria-label="X'te paylaş"
          data-testid="column-share-twitter"
          className="p-2 bg-black hover:bg-gray-800 text-white rounded-full"
        ><Twitter className="w-4 h-4" /></a>
        <a
          href={`https://wa.me/?text=${shareTitle} ${enc(shareUrl)}`}
          target="_blank" rel="noreferrer"
          aria-label="WhatsApp'ta paylaş"
          data-testid="column-share-whatsapp"
          className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-full"
        ><Send className="w-4 h-4" /></a>
        <a
          href={`https://t.me/share/url?url=${enc(shareUrl)}&text=${shareTitle}`}
          target="_blank" rel="noreferrer"
          aria-label="Telegram'da paylaş"
          data-testid="column-share-telegram"
          className="p-2 bg-sky-500 hover:bg-sky-600 text-white rounded-full"
        ><Send className="w-4 h-4" /></a>
        <StoryPreviewButton
          url={storyUrl}
          filename={`sine81-yazi-${col.slug}.png`}
          title={col.title}
          text={`${col.title} — SİNE81`}
          testIdShare="column-share-story"
          testIdPreview="column-share-story-preview"
        />
        <button
          type="button"
          onClick={copy}
          data-testid="column-share-copy"
          className="ml-1 inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-gray-200 hover:bg-gray-50 rounded-full text-xs font-semibold text-gray-700"
        >Linki Kopyala</button>
      </div>
    </article>
  );
}
